import { memo, useEffect, useMemo, type ReactNode } from 'react'
import { Interactive } from '@highlight/ui'
import { ManageSubscriptionModal } from './ManageSubscriptionModal'
import { RequestContextModal } from './RequestContextModal'
import { useModals, useOpenModals } from '../../store'
import { LoginModal } from './LoginModal'
import { GatedTopUpModal } from './gated-top-up-modal'
import { ModalObjectProps } from '../../types'

// Register the modal components by ID here
const ModalMap: Record<string, React.FC<ModalObjectProps>> = {
  'request-context-modal': RequestContextModal,
  'login-modal': LoginModal,
  'manage-subscription': ManageSubscriptionModal,
  'top-up': GatedTopUpModal,
}

interface ModalRendererProps {
  /** A replacement to render in an open modal's place, or null to render the modal itself. */
  intercept?: (modal: ModalObjectProps) => ReactNode | null
}

export const ModalRenderer = memo(({ intercept }: ModalRendererProps) => {
  const modals = useOpenModals()
  const { openModal, closeModal } = useModals()

  useEffect(() => {
    // @ts-ignore
    window._modals = {
      openModal,
      closeModal,
    }
    return () => {
      // @ts-ignore
      window._modals = undefined
    }
  }, [openModal, closeModal])

  return useMemo(() => {
    return modals.map((modal) => {
      const { id, context } = modal
      const replacement = intercept?.(modal)
      if (replacement) {
        return <Interactive key={id}>{replacement}</Interactive>
      }
      const ModalComponent = ModalMap[id]
      if (!ModalComponent) {
        console.warn('Unsupported modal id in ModalRenderer:', id, typeof ModalComponent)
        return null
      }
      // These modals draw their own full-overlay scrims. In the overlay window
      // the scrim only takes clicks inside registered bounds, so without this
      // the close button falls through to the app behind it.
      return (
        <Interactive key={id}>
          <ModalComponent id={id} context={context} />
        </Interactive>
      )
    })
  }, [modals, intercept])
})
